import { useEffect, useMemo, useState } from "react";
import type { DashboardData, Owner } from "../types";
import { reconciliationDueMonth, reconciliationState } from "../lib/reconciliation";

/**
 * The month-end balance review: which month is due, and whether its sheet is open. The sheet opens
 * by itself the first time each session that a due month is incomplete.
 */
export function useReconciliationPrompt({ data, profile, today }: {
  data: DashboardData | null;
  profile: Owner | null;
  today: string;
}) {
  const [reconciliationOpen, setReconciliationOpen] = useState(false);
  // Walks every account's full ledger, so it must not rerun on every keystroke — only when the data
  // or the calendar day changes.
  const dueReconciliationMonth = useMemo(
    () => (data ? reconciliationDueMonth(data, today) : ""),
    [data, today],
  );
  useEffect(() => {
    if (!data || !profile || !dueReconciliationMonth) return;
    const state = reconciliationState(data, dueReconciliationMonth);
    const key = `barnyard-reconciliation-seen-${profile}-${dueReconciliationMonth}`;
    if (!state.complete && !sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      setReconciliationOpen(true);
    }
  // Keyed on the load time, not the data object, so editing a form does not reopen the prompt.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.fetchedAt, profile, dueReconciliationMonth]);
  return { dueReconciliationMonth, reconciliationOpen, setReconciliationOpen };
}
